import React, { useState } from "react";
import { Row, Col, Card, Button, Table, Accordion, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { RouteDefinitions } from "../../../AppRoutes";
import { useCTISSchema } from "./CTISSchemaProvider";

export default function CTISHomePage() {
  const navigate = useNavigate();
  const { screenIndex } = useCTISSchema(); // Access live EMA metadata
  
  const ctisModule = RouteDefinitions.find(route => route.path === "/ctis");
  const subPages = ctisModule ? ctisModule.children : [];

  const categories = [
    {
      id: "admin",
      name: "Administrative",
      description: "Core trial data and the Initial Application forms.",
      paths: ["overview", "member-state-overlays", "item-register"]
    },
    {
      id: "content",
      name: "Dossier Content",
      description: "Primary document placeholders (Protocol, IB, Informed Consent, etc.).",
      paths: ["templates"] // Mapping to the dynamic template renderer
    },
    {
      id: "lifecycle",
      name: "Lifecycle & Validation",
      description: "Management of rules, package assembly, and EMA readiness.",
      paths: ["rules", "field-limits", "package-builder", "validation"]
    }
  ];

  const [activeKeys, setActiveKeys] = useState([]);

  const handleToggle = (id) => {
    if (activeKeys.includes(id)) {
      setActiveKeys(activeKeys.filter(key => key !== id));
    } else {
      setActiveKeys([...activeKeys, id]);
    }
  };

  const toggleAll = () => {
    if (activeKeys.length === categories.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(categories.map(c => c.id));
    }
  };

  return (
    <>
      <div className="mb-4">
        {/* EMA STANDARDS EXPLANATORY TEXT */}
        <Card className="border-0 shadow-sm mb-4 bg-light">
          <Card.Body>
            <h2 className="h6 fw-bold mb-3 text-uppercase text-dark" style={{ letterSpacing: '1px', opacity: 0.8 }}>
              EMA Standards: Screen Blueprints
            </h2>
            <Table responsive hover className="bg-white rounded overflow-hidden mb-0 border">
              <thead className="table-dark">
                <tr>
                  <th>Category</th>
                  <th>Template Files</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody className="small">
                <tr>
                  <td className="fw-bold">Administrative</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Overview, MSCs, Form_IN</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Core trial data, MSCs, and Initial Application form.</td>
                </tr>
                <tr>
                  <td className="fw-bold">Dossier Content</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Part_I, Part_II</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Primary document placeholders (Protocol, IB, etc.).</td>
                </tr>
                <tr>
                  <td className="fw-bold">Lifecycle</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Form_SM, Form_non_SM, RFI</td>
                  <td className="text-dark" style={{ fontWeight: '500' }}>Substantial modifications and regulatory queries.</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end mb-3">
          <Button variant="dark" size="sm" className="fw-bold px-3" onClick={toggleAll}>
            {activeKeys.length === categories.length ? "COLLAPSE ALL" : "EXPAND ALL"}
          </Button>
        </div>

        <Accordion activeKey={activeKeys} alwaysOpen>
          {categories.map((cat) => (
            <div key={cat.id} className="mb-3">
              <div 
                className="d-flex justify-content-between align-items-center ps-3 pe-3 py-3 bg-white border rounded shadow-sm" 
                style={{ cursor: "pointer", borderLeft: activeKeys.includes(cat.id) ? '4px solid #212529' : '1px solid #dee2e6' }}
                onClick={() => handleToggle(cat.id)}
              >
                <h2 className="h5 fw-bold mb-0 text-dark">{cat.name}</h2>
                <span className={`badge ${activeKeys.includes(cat.id) ? 'bg-dark' : 'bg-secondary'}`}>
                  {activeKeys.includes(cat.id) ? 'CLOSE' : 'OPEN'}
                </span>
              </div>
              
              <Accordion.Collapse eventKey={cat.id}>
                <div className="p-4 bg-white border-start border-end border-bottom rounded-bottom mb-4 shadow-sm">
                  <p className="text-dark mb-4 fst-italic" style={{ fontSize: '0.9rem', opacity: 0.85, fontWeight: '500' }}>
                    {cat.description}
                  </p>
                  <Row className="g-3">
                    {/* Render standard sub-pages defined in AppRoutes */}
                    {subPages
                      .filter(page => cat.paths.includes(page.path))
                      .map((page) => (
                        <Col xs={12} md={6} xl={4} key={page.path}>
                          <Card className="h-100 border-0 home-card shadow-sm" style={{ background: '#f8f9fa' }}>
                            <Card.Body className="d-flex flex-column">
                              <div className="mb-1 text-primary fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                {page.path.toUpperCase()}
                              </div>
                              <h3 className="h6 fw-bold mb-3 text-dark">{page.handle?.crumb}</h3>
                              <Button 
                                variant="dark" 
                                size="sm" 
                                className="mt-auto fw-bold py-2" 
                                style={{ fontSize: '0.75rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/ctis/${page.path}`);
                                }}
                              >
                                OPEN MODULE
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}

                    {/* DYNAMIC: Render EMA Templates if this is the "Dossier Content" category */}
                    {cat.id === "content" && screenIndex?.screens.map((screen) => (
                      <Col xs={12} md={6} xl={4} key={screen.screen_id}>
                        <Card className="h-100 border-0 home-card shadow-sm" style={{ background: '#f8f9fa', borderRight: '3px solid #0d6efd' }}>
                          <Card.Body className="d-flex flex-column">
                            <div className="d-flex justify-content-between">
                              <div className="mb-1 text-primary fw-bold" style={{ fontSize: '0.65rem' }}>
                                EMA BLUEPRINT: {screen.screen_id.toUpperCase()}
                              </div>
                              <Badge bg="info" className="text-dark" style={{ fontSize: '0.6rem' }}>
                                {screen.field_count} Fields
                              </Badge>
                            </div>
                            <h3 className="h6 fw-bold mb-3 text-dark">{screen.label}</h3>
                            <Button 
                              variant="primary" 
                              size="sm" 
                              className="mt-auto fw-bold py-2" 
                              style={{ fontSize: '0.75rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/ctis/templates/${screen.screen_id}`);
                              }}
                            >
                              VIEW BLUEPRINT
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Accordion.Collapse>
            </div>
          ))}
        </Accordion>
      </div>
    </>
  );
}